import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import ErrorIcon from '@components/icons/StatusError.svg';
import PendingIcon from '@components/icons/StatusPending.svg';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useTransactionProcessingContext } from '@providers/TransactionProcessingProvider';
import { useStripe } from '@stripe/react-stripe-js';
import { PaymentService } from 'app/API';
import PositiveIcon from 'app/components/icons/Positive.svg';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Content } from './styles/Content';

type TProps = {
  billingAccountId?: string;
  chargeId?: string;
  paymentIntentId?: string;
  pics: string;
  onSuccess: () => void;
};

type TransactionOutcome = 'pending' | 'credited' | 'settled' | 'failed' | 'indeterminate';

const normalizeId = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const getMessage = (outcome: TransactionOutcome): string => {
  if (outcome === 'credited') {
    return 'Your payment has been processed successfully';
  }
  if (outcome === 'settled') {
    return 'Your payment went through. Your balance will update shortly.';
  }
  if (outcome === 'failed') {
    return 'Something went wrong. Please try again later';
  }
  if (outcome === 'indeterminate') {
    return 'Your payment is still processing. Your balance will update automatically.';
  }
  return 'Your payment is pending';
};

export const TransactionStatusModalContent = ({
  billingAccountId,
  chargeId,
  paymentIntentId,
  onSuccess,
  pics,
}: TProps) => {
  const stripe = useStripe();
  const { call } = useAPI();
  const { updateMe } = useAuthContext();
  const { closeModal } = useModalContext();
  const {
    transactionStatusDto,
    transactionResults,
    scheduleBalanceReconciliation,
    showTransactionProcessingIndicator,
    markTransactionProcessingFailed,
  } = useTransactionProcessingContext();
  const location = useLocation();
  const [outcome, setOutcome] = useState<TransactionOutcome>('pending');
  const [resolvedChargeId, setResolvedChargeId] = useState(() => normalizeId(chargeId));

  const mountedRef = useRef(true);
  const outcomeRef = useRef<TransactionOutcome>('pending');
  const resolvedChargeIdRef = useRef(resolvedChargeId);
  const transactionResultsRef = useRef(transactionResults);
  const stripePollAttemptsRef = useRef(0);
  const chargeLookupAttemptsRef = useRef(0);
  const stripeRequestGenerationRef = useRef(0);
  const chargeLookupGenerationRef = useRef(0);
  const stripePollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const chargeLookupTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const settledRefreshStartedRef = useRef(false);
  const reconciliationScheduledRef = useRef(false);
  const finalizedRef = useRef(false);
  const initialPathnameRef = useRef(location.pathname);
  const currentPathnameRef = useRef(location.pathname);
  const closeModalRef = useRef(closeModal);
  const billingAccountIdRef = useRef(normalizeId(billingAccountId));
  const onSuccessRef = useRef(onSuccess);
  const paymentIntentIdRef = useRef(normalizeId(paymentIntentId));
  const showTransactionProcessingIndicatorRef = useRef(showTransactionProcessingIndicator);

  resolvedChargeIdRef.current = resolvedChargeId;
  transactionResultsRef.current = transactionResults;
  currentPathnameRef.current = location.pathname;
  closeModalRef.current = closeModal;
  billingAccountIdRef.current = normalizeId(billingAccountId);
  onSuccessRef.current = onSuccess;
  paymentIntentIdRef.current = normalizeId(paymentIntentId);
  showTransactionProcessingIndicatorRef.current = showTransactionProcessingIndicator;

  const clearStripePoll = useCallback(() => {
    if (stripePollTimerRef.current) {
      clearInterval(stripePollTimerRef.current);
      stripePollTimerRef.current = null;
    }
  }, []);

  const clearChargeLookup = useCallback(() => {
    if (chargeLookupTimerRef.current) {
      clearInterval(chargeLookupTimerRef.current);
      chargeLookupTimerRef.current = null;
    }
  }, []);

  const enterOutcome = useCallback(
    (nextOutcome: TransactionOutcome) => {
      const currentOutcome = outcomeRef.current;
      if (currentOutcome === 'credited' || currentOutcome === 'failed') {
        return;
      }
      if (currentOutcome === 'indeterminate' && nextOutcome !== 'credited' && nextOutcome !== 'failed') {
        return;
      }
      if (currentOutcome === 'settled' && nextOutcome === 'pending') {
        return;
      }

      outcomeRef.current = nextOutcome;
      setOutcome(nextOutcome);

      if (nextOutcome === 'failed') {
        markTransactionProcessingFailed({
          chargeId: resolvedChargeIdRef.current ?? undefined,
          paymentIntentId: paymentIntentIdRef.current ?? undefined,
        });
      }

      if (nextOutcome === 'settled') {
        stripeRequestGenerationRef.current += 1;
        clearStripePoll();
        if (!settledRefreshStartedRef.current) {
          settledRefreshStartedRef.current = true;
          void updateMe({ includeNotifications: false });
        }
      }

      if (nextOutcome === 'credited' || nextOutcome === 'failed' || nextOutcome === 'indeterminate') {
        stripeRequestGenerationRef.current += 1;
        chargeLookupGenerationRef.current += 1;
        clearStripePoll();
        clearChargeLookup();
      }
    },
    [clearChargeLookup, clearStripePoll, updateMe, markTransactionProcessingFailed],
  );

  const loadStripeStatus = useCallback(async () => {
    if (!stripe) {
      console.error('Stripe is not loaded');
      if (stripePollAttemptsRef.current >= 10) {
        enterOutcome('indeterminate');
      }
      return;
    }

    const generation = stripeRequestGenerationRef.current + 1;
    stripeRequestGenerationRef.current = generation;

    try {
      const result = await stripe.retrievePaymentIntent(pics);
      if (!mountedRef.current || stripeRequestGenerationRef.current !== generation) {
        return;
      }

      const currentOutcome = outcomeRef.current;
      if (currentOutcome === 'credited' || currentOutcome === 'failed' || currentOutcome === 'indeterminate') {
        return;
      }

      const stripeStatus = result.paymentIntent?.status;
      if (stripeStatus === 'succeeded') {
        const currentChargeId = resolvedChargeIdRef.current;
        const latchedResult = currentChargeId ? transactionResultsRef.current.get(currentChargeId) : undefined;
        enterOutcome(latchedResult?.status === 'success' ? 'credited' : 'settled');
        return;
      }

      if (stripeStatus === 'requires_payment_method' || stripeStatus === 'canceled') {
        if (outcomeRef.current !== 'settled') {
          enterOutcome('failed');
        }
        return;
      }

      if (stripePollAttemptsRef.current >= 10) {
        enterOutcome('indeterminate');
      }
    } catch (error) {
      if (!mountedRef.current || stripeRequestGenerationRef.current !== generation) {
        return;
      }
      console.error('Unable to retrieve payment status', error);
      if (stripePollAttemptsRef.current >= 10) {
        enterOutcome('indeterminate');
      }
    }
  }, [enterOutcome, pics, stripe]);

  useEffect(() => {
    if (reconciliationScheduledRef.current) {
      return;
    }
    reconciliationScheduledRef.current = true;
    scheduleBalanceReconciliation(billingAccountIdRef.current);
  }, [scheduleBalanceReconciliation]);

  useEffect(() => {
    const currentChargeId = resolvedChargeIdRef.current;
    if (!currentChargeId) {
      return;
    }

    const result =
      transactionStatusDto?.id === currentChargeId ? transactionStatusDto : transactionResults.get(currentChargeId);
    if (result?.status === 'success') {
      enterOutcome('credited');
    } else if (result?.status === 'error') {
      enterOutcome('failed');
    }
  }, [enterOutcome, resolvedChargeId, transactionResults, transactionStatusDto]);

  useEffect(() => {
    if (outcome !== 'pending') {
      clearStripePoll();
      return;
    }

    stripePollTimerRef.current = setInterval(() => {
      if (stripePollAttemptsRef.current >= 10) {
        clearStripePoll();
        return;
      }
      stripePollAttemptsRef.current += 1;
      void loadStripeStatus();
    }, 3000);

    const deadline = setTimeout(() => {
      if (outcomeRef.current === 'pending') {
        enterOutcome('indeterminate');
      }
    }, 45000);

    return () => {
      clearStripePoll();
      clearTimeout(deadline);
    };
  }, [clearStripePoll, enterOutcome, loadStripeStatus, outcome]);

  useEffect(() => {
    const normalizedPaymentIntentId = normalizeId(paymentIntentId);
    if (resolvedChargeId || !normalizedPaymentIntentId) {
      clearChargeLookup();
      return;
    }

    chargeLookupTimerRef.current = setInterval(() => {
      const currentOutcome = outcomeRef.current;
      if (
        currentOutcome === 'credited' ||
        currentOutcome === 'failed' ||
        currentOutcome === 'indeterminate' ||
        chargeLookupAttemptsRef.current >= 10
      ) {
        clearChargeLookup();
        return;
      }

      chargeLookupAttemptsRef.current += 1;
      const generation = chargeLookupGenerationRef.current + 1;
      chargeLookupGenerationRef.current = generation;

      void call(() => PaymentService.getPaymentIntent({ id: normalizedPaymentIntentId }))
        .then((paymentIntent) => {
          if (!mountedRef.current || chargeLookupGenerationRef.current !== generation) {
            return;
          }

          const nextChargeId = normalizeId(paymentIntent.latest_charge);
          if (!nextChargeId) {
            if (chargeLookupAttemptsRef.current >= 10) {
              clearChargeLookup();
            }
            return;
          }

          resolvedChargeIdRef.current = nextChargeId;
          setResolvedChargeId(nextChargeId);
          clearChargeLookup();

          const latchedResult = transactionResultsRef.current.get(nextChargeId);
          if (latchedResult?.status === 'success') {
            enterOutcome('credited');
          } else if (latchedResult?.status === 'error') {
            enterOutcome('failed');
          }
        })
        .catch((error) => {
          if (!mountedRef.current || chargeLookupGenerationRef.current !== generation) {
            return;
          }
          console.error('Unable to retrieve payment intent', error);
          if (chargeLookupAttemptsRef.current >= 10) {
            clearChargeLookup();
          }
        });
    }, 3000);

    return clearChargeLookup;
  }, [call, clearChargeLookup, enterOutcome, paymentIntentId, resolvedChargeId]);

  const finalize = useCallback(() => {
    if (finalizedRef.current) {
      return;
    }
    finalizedRef.current = true;
    clearStripePoll();
    clearChargeLookup();
    stripeRequestGenerationRef.current += 1;
    chargeLookupGenerationRef.current += 1;

    const currentOutcome = outcomeRef.current;
    const pathChanged = initialPathnameRef.current !== currentPathnameRef.current;

    if (currentOutcome === 'credited') {
      closeModalRef.current();
      if (!pathChanged) {
        onSuccessRef.current();
      }
      return;
    }

    if (currentOutcome === 'failed') {
      if (!pathChanged) {
        closeModalRef.current();
      }
      return;
    }

    if (!pathChanged) {
      closeModalRef.current();
    }
    showTransactionProcessingIndicatorRef.current({
      billingAccountId: billingAccountIdRef.current,
      chargeId: resolvedChargeIdRef.current,
      paymentIntentId: paymentIntentIdRef.current,
      settled: outcomeRef.current === 'settled',
    });
  }, [clearChargeLookup, clearStripePoll]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      finalize();
    };
  }, [finalize]);

  const pendingAppearance = outcome === 'pending' || outcome === 'settled' || outcome === 'indeterminate';

  return (
    <ModalWrapper width="390px" onCancel={finalize}>
      <ModalTitle>Payment Status</ModalTitle>
      <Content>
        {outcome === 'credited' && (
          <div className="icon">
            <PositiveIcon />
          </div>
        )}
        {pendingAppearance && (
          <div className="icon">
            <PendingIcon />
          </div>
        )}
        {outcome === 'failed' && (
          <div className="icon">
            <ErrorIcon />
          </div>
        )}
        <p className="description">{getMessage(outcome)}</p>
        <div className="buttonContainer" style={{ width: pendingAppearance ? 'auto' : '130px' }}>
          {outcome === 'pending' ? (
            <OutlinedButton className="w-full" onClick={() => void loadStripeStatus()}>
              Refresh
            </OutlinedButton>
          ) : null}
          <Button className="w-full" onClick={finalize}>
            {outcome === 'credited' || outcome === 'failed' ? 'Ok' : 'Close'}
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
